/**
 * Orders.
 *
 * One request feeds the whole screen. `GET /orders/page` returns the tiles, the eight queue
 * counts, the rows, the payments with no order and the auto-widen notice together, so the
 * number on a tile, the number in the rail and the rows in the table are one derivation in
 * Postgres rather than four that drift by lunchtime.
 *
 * Every control writes to the URL. The search box, the range chips, the rail, the filters
 * and the sort are all query parameters, which is what makes a filtered view a real link,
 * makes Back undo a filter instead of leaving the screen, and makes a reload land where it
 * left off.
 *
 * Nothing on this page polls. The `/changes` loop already invalidates `['orders']` within
 * ten seconds of the tablet packing something or the matcher matching something, and a
 * second clock here would only add requests and disagree with the first.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useRouterState, useSearch } from '@tanstack/react-router';
import type { BulkReprintOutcome, OrderQueue, PrintDoc, UnmatchedPaymentRow } from '@elutide/shared';
import {
  Button,
  Card,
  Chip,
  DownloadIcon,
  ErrorState,
  Skeleton,
  useToast,
} from '@/components';
import { ApiProblem } from '@/lib/api';
import { useSession } from '@/app/session';
import { downloadCsv } from '@/lib/csv';
import { indexLegend } from '@/lib/legend';
import { isTypingTarget, useIsPhone, useNow } from '@/lib/hooks';
import { OrdersKpiStrip } from './OrdersKpiStrip';
import { OrdersRail } from './OrdersRail';
import { OrdersTable } from './OrdersTable';
import { OrderCardList } from './OrderCardList';
import { OrdersBulkBar, type BulkStatusAction } from './OrdersBulkBar';
import { ActiveFilterChips, OrdersToolbar } from './OrdersToolbar';
import { UnmatchedPayments } from './UnmatchedPayments';
import { OrderDrawer, type DrawerSlots } from './drawer/OrderDrawer';
import { OrderActions, type MarkPaidPrefill, type PrimaryForm } from './drawer/OrderActions';
import {
  AddressEditor,
  CreditGrant,
  HoldControl,
  LineEditor,
  NoteComposer,
  PaymentActions,
  TrackingEditor,
} from './drawer/sectionActions';
import { ordersApi, reprintMany } from './api';
import { useInvalidateOrder, useOrdersLegend, useOrdersPage } from './queries';
import { rowsToCsv } from './csv';
import {
  DEFAULT_SEARCH,
  activeFilterCount,
  parseOrdersSearch,
  toSearchParams,
  type OrdersSearch,
  type OrdersSort,
} from './searchParams';
import {
  ATTENTION_SECTION,
  QUEUE_LABELS,
  RAIL_QUEUES,
  type DrawerSection,
  type OrdersPageRowView as OrdersPageRow,
} from './types';
import { safeTrackingUrl, type RowHandlers } from './cells';
import './orders.css';

export interface OrdersPageProps {
  /** Set when the URL is /orders/:orderId. The drawer opens over the list. */
  orderId?: number | null;
  onOpenOrder?: (orderId: number | null) => void;
}

const ALL_PRINT_DOCS: readonly PrintDoc[] = ['packing_slip', 'coa', 'label'];

export function OrdersPage({ orderId = null, onOpenOrder }: OrdersPageProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const now = useNow(30_000);
  const invalidate = useInvalidateOrder();
  const { can } = useSession();
  /**
   * The API refuses a bulk status change, an export or a mark paid from a role without
   * the permission, and the packer's response has no money to mark against anyway. The
   * buttons go rather than 403 at the second click.
   */
  const canBulk = can('orders.bulk');
  const canExport = can('orders.export');
  const canMarkPaid = can('orders.mark_paid');

  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const rawSearch = useSearch({ strict: false }) as Record<string, unknown>;
  const search = useMemo(() => parseOrdersSearch(rawSearch), [rawSearch]);

  const page = useOrdersPage(search);
  const legendQuery = useOrdersLegend();
  const legend = useMemo(() => indexLegend(legendQuery.data), [legendQuery.data]);

  const [selected, setSelected] = useState<ReadonlySet<number>>(() => new Set<number>());
  const [rowMenu, setRowMenu] = useState<number | null>(null);
  const [pending, setPending] = useState<ReadonlySet<number>>(() => new Set<number>());
  const [extending, setExtending] = useState<ReadonlySet<number>>(() => new Set<number>());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResults, setBulkResults] = useState<BulkReprintOutcome[] | null>(null);
  const [drawerSection, setDrawerSection] = useState<DrawerSection | undefined>(undefined);
  const [drawerArmed, setDrawerArmed] = useState<PrimaryForm>(null);
  const [drawerPrefill, setDrawerPrefill] = useState<MarkPaidPrefill | null>(null);

  /**
   * Write the whole search back to the URL in one navigation.
   *
   * `replace` is for the search box. Typing pushes a debounced update roughly once a pause,
   * and pushing each one would make Back walk backwards through half-typed names before it
   * reached the thing the operator actually wants to undo. Every structural change, the
   * queue, the range, the filters and the sort, is a real history entry, because those are
   * what Back is for on this screen.
   */
  const setSearch = useCallback(
    (next: OrdersSearch, options: { replace?: boolean } = {}) => {
      void navigate({
        to: pathname,
        search: toSearchParams(next),
        replace: options.replace ?? false,
      });
    },
    [navigate, pathname],
  );

  const setQueue = useCallback(
    (queue: OrderQueue) => {
      setSelected(new Set<number>());
      setSearch({ ...search, queue });
    },
    [search, setSearch],
  );

  /**
   * Open the drawer, optionally at a section and with a form already armed.
   *
   * Mark paid from a row and "This is it" on an unmatched payment both land here: the row
   * has no order version and no payment facts, so the write cannot happen from the table,
   * but the operator should not then have to hunt for the button either. The drawer opens
   * at Payment with Mark paid already open.
   */
  const openOrder = useCallback(
    (
      nextId: number | null,
      section?: DrawerSection,
      armed: PrimaryForm = null,
      prefill: MarkPaidPrefill | null = null,
    ) => {
      setDrawerSection(section);
      setDrawerArmed(nextId === null ? null : armed);
      setDrawerPrefill(nextId === null ? null : prefill);
      onOpenOrder?.(nextId);
    },
    [onOpenOrder],
  );

  /**
   * The rail's number keys, and Escape to drop a selection.
   *
   * Bound on the document but ignored while the caret is in a field, so typing `3` into the
   * search box searches for a 3 rather than jumping to To print.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target) || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === 'Escape') {
        setSelected(new Set<number>());
        return;
      }
      const index = Number(event.key);
      if (!Number.isInteger(index) || index < 1 || index > RAIL_QUEUES.length) return;
      const queue = RAIL_QUEUES[index - 1];
      if (!queue) return;
      event.preventDefault();
      setQueue(queue);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [setQueue]);

  /**
   * The totals come from the first page and the rows from all of them.
   *
   * Every page repeats the tiles, the queue counts, the thresholds and the unmatched
   * payments, because they are answers about the whole queue rather than about this
   * hundred rows. Reading them from the newest page would make them flicker on Load more.
   */
  const data = page.data?.pages[0] ?? null;
  const rows = useMemo<OrdersPageRow[]>(
    () => page.data?.pages.flatMap((chunk) => chunk.rows) ?? [],
    [page.data],
  );
  /**
   * Absent for a packer and an agent (`redact.ts`), and absent means the block does not
   * render at all: the synthetic rows are never attempted from an undefined list.
   */
  const unmatched = useMemo(() => data?.unmatched_payments ?? [], [data]);

  /** Selection never outlives the rows it points at. */
  useEffect(() => {
    setSelected((current) => {
      if (current.size === 0) return current;
      const live = new Set(rows.map((row) => row.order_id));
      const next = new Set([...current].filter((id) => live.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [rows]);

  const mark = (set: (updater: (current: ReadonlySet<number>) => Set<number>) => void, id: number, on: boolean) =>
    set((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const problemFrom = (error: unknown): ApiProblem | null =>
    error instanceof ApiProblem ? error : null;

  const failToast = useCallback(
    (error: unknown, fallback: string) => {
      const problem = problemFrom(error);
      toast.push({
        tone: 'crit',
        message: problem?.message ?? (error instanceof Error ? error.message : fallback),
        requestId: problem?.shortRequestId ?? null,
      });
    },
    [toast],
  );

  /* ---- row actions ------------------------------------------------- */

  const onReprintRow = useCallback(
    async (row: OrdersPageRow, docs: readonly PrintDoc[]) => {
      mark(setPending, row.order_id, true);
      const [result] = await reprintMany([row.order_id], docs);
      mark(setPending, row.order_id, false);
      if (!result) return;
      toast.push({
        tone: result.ok ? 'ok' : 'crit',
        message: result.ok
          ? result.replayed
            ? `Order ${row.number} was already queued with that request.`
            : `Order ${row.number} sent to the printer.`
          : `Order ${row.number} did not reprint. ${result.error ?? ''}`.trim(),
      });
      invalidate(row.order_id);
    },
    [invalidate, toast],
  );

  const onQuickAction = useCallback(
    async (row: OrdersPageRow) => {
      switch (row.quick_action) {
        case 'print': {
          mark(setPending, row.order_id, true);
          try {
            await ordersApi.print(row.order_id, ALL_PRINT_DOCS);
            toast.push({ tone: 'ok', message: `Order ${row.number} sent to the print queue.` });
            invalidate(row.order_id);
          } catch (error) {
            failToast(error, 'The print job was not accepted.');
          } finally {
            mark(setPending, row.order_id, false);
          }
          return;
        }
        case 'track': {
          const url = safeTrackingUrl(row.fulfilment.tracking_url);
          if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
            return;
          }
          openOrder(row.order_id, 'fulfilment');
          return;
        }
        case 'review': {
          const reason = row.attention_reasons[0];
          openOrder(row.order_id, reason ? ATTENTION_SECTION[reason] : 'flags');
          return;
        }
        case 'mark_paid':
        default:
          // Mark paid needs the order's version and its payment facts, and neither is on a
          // row. The drawer has both, so it opens with the form already up: one click to
          // the form, one to send it, which is what acceptance test 4 asks for. A role
          // that cannot mark paid lands on the Payment section with nothing armed.
          openOrder(row.order_id, 'payment', canMarkPaid ? 'mark_paid' : null);
      }
    },
    [canMarkPaid, failToast, invalidate, openOrder, toast],
  );

  const onExtend = useCallback(
    async (row: OrdersPageRow) => {
      mark(setExtending, row.order_id, true);
      try {
        const result = await ordersApi.extendAutoCancel(row.order_id, { note: null });
        toast.push({
          tone: 'ok',
          message: `Order ${row.number} now cancels ${new Date(result.autocancel_at).toLocaleString()}.`,
        });
        invalidate(row.order_id);
      } catch (error) {
        failToast(error, 'The deadline was not moved.');
      } finally {
        mark(setExtending, row.order_id, false);
      }
    },
    [failToast, invalidate, toast],
  );

  const handlers = useMemo<RowHandlers>(
    () => ({
      now,
      onQuickAction: (row) => void onQuickAction(row),
      onReprint: (row, docs) => void onReprintRow(row, docs),
      onExtend: (row) => void onExtend(row),
      openMenu: rowMenu,
      onToggleMenu: (id) => setRowMenu((current) => (id !== null && current === id ? null : id)),
      isActing: (id) => pending.has(id),
      isExtending: (id) => extending.has(id),
    }),
    [extending, now, onExtend, onQuickAction, onReprintRow, pending, rowMenu],
  );

  /* ---- bulk -------------------------------------------------------- */

  const selectedIds = useMemo(() => [...selected], [selected]);

  const onBulkReprint = useCallback(
    async (docs: readonly PrintDoc[]) => {
      setBulkBusy(true);
      const results = await reprintMany(selectedIds, docs);
      setBulkBusy(false);
      setBulkResults(results);
      setSelected(new Set<number>());
      invalidate();
    },
    [invalidate, selectedIds],
  );

  const onBulkStatus = useCallback(
    async (action: BulkStatusAction) => {
      setBulkBusy(true);
      try {
        const result = await ordersApi.bulk(action, selectedIds, {}, 'Bulk action from Orders');
        const ok = result.results.filter((entry) => entry.ok).length;
        toast.push({
          tone: ok === result.results.length ? 'ok' : 'warn',
          message: `${ok} of ${result.results.length} orders updated.`,
        });
        setSelected(new Set<number>());
        invalidate();
      } catch (error) {
        failToast(error, 'The bulk action did not run.');
      } finally {
        setBulkBusy(false);
      }
    },
    [failToast, invalidate, selectedIds, toast],
  );

  const onExport = useCallback(() => {
    const stamp = new Date().toISOString().slice(0, 10);
    const started = downloadCsv(`orders-${search.queue}-${stamp}.csv`, rowsToCsv(rows));
    toast.push({
      tone: started ? 'ok' : 'warn',
      message: started
        ? `${rows.length} rows exported.`
        : 'This browser would not start the download.',
    });
  }, [rows, search.queue, toast]);

  /* ---- unmatched payments ------------------------------------------ */

  /**
   * "This is it" on a payment with no order.
   *
   * The payment's amount, its payer and the memo the bank sent are what the Mark paid form
   * asks for, and they are on screen two lines above the button. Carrying them into the
   * form is the whole point of the block: without it the operator reads them off one panel
   * and types them into another.
   */
  const onClaim = useCallback(
    (payment: UnmatchedPaymentRow, candidateId: number) => {
      openOrder(candidateId, 'payment', 'mark_paid', {
        amount_cents: payment.amount_cents,
        reference: payment.memo ?? payment.payer ?? '',
        payer: payment.payer,
        source: payment.source,
      });
    },
    [openOrder],
  );

  /* ---- drawer ------------------------------------------------------ */

  /**
   * Everything the drawer can change.
   *
   * The sections are read-only components; every control that writes is injected here. That
   * split is what let the page ship before the actions did, and it is what keeps a layout
   * change out of the blast radius of an action change.
   */
  const drawerSlots = useMemo<DrawerSlots>(() => {
    const changed = (id: number) => () => invalidate(id);
    // Keyed by order id. Without it a half typed refund on one order would still be on
    // screen, with its amount, when the next order's drawer opened in the same position.
    return {
      primary: (d) => (
        <OrderActions
          key={d.header.order_id}
          detail={d}
          onChanged={changed(d.header.order_id)}
          armed={drawerArmed}
          prefill={drawerPrefill}
        />
      ),
      payment: (d) => (
        <PaymentActions key={d.header.order_id} detail={d} onChanged={changed(d.header.order_id)} />
      ),
      tracking: (d) => (
        <TrackingEditor key={d.header.order_id} detail={d} onChanged={changed(d.header.order_id)} />
      ),
      items: (d) => (
        <LineEditor key={d.header.order_id} detail={d} onChanged={changed(d.header.order_id)} />
      ),
      customer: (d) => (
        <AddressEditor key={d.header.order_id} detail={d} onChanged={changed(d.header.order_id)} />
      ),
      credit: (d) => (
        <CreditGrant key={d.header.order_id} detail={d} onChanged={changed(d.header.order_id)} />
      ),
      activity: (d) => (
        <NoteComposer key={d.header.order_id} detail={d} onChanged={changed(d.header.order_id)} />
      ),
      flags: (d) => (
        <HoldControl key={d.header.order_id} detail={d} onChanged={changed(d.header.order_id)} />
      ),
    };
  }, [drawerArmed, drawerPrefill, invalidate]);

  /* ---- render ------------------------------------------------------ */

  const problem = problemFrom(page.error);
  const showSkeleton = page.isLoading && !data;

  if (showSkeleton) {
    return (
      <div className="ord-page">
        <Skeleton shape="card" lines={4} label="Loading orders" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="ord-page">
        <ErrorState
          message={problem ? problem.message : 'The orders page did not load.'}
          requestId={problem ? problem.shortRequestId : null}
          onRetry={() => void page.refetch()}
        />
      </div>
    );
  }

  const stale = page.isFetching;
  /** True when the rows are a subset of the queue, so its total is a different question. */
  const narrowed = search.q !== '' || activeFilterCount(search) > 0 || search.range !== 'all';
  const emptyAction =
    search.q !== '' || search.range !== DEFAULT_SEARCH.range ? (
      <Button size="sm" variant="ghost" onClick={() => setSearch({ ...search, range: 'all' })}>
        Search all time
      </Button>
    ) : undefined;

  return (
    <div className="ord-page">
      <OrdersKpiStrip kpis={data.kpis} legend={legend} queue={search.queue} onQueue={setQueue} />

      <OrdersToolbar
        search={search}
        onSearch={setSearch}
        widened={data.widened}
        widenedCount={rows.length}
        busy={stale}
      />

      <ActiveFilterChips search={search} onSearch={setSearch} />

      {data.unmatched_payments ? (
        <UnmatchedPayments
          rows={unmatched}
          now={now}
          expanded={search.queue === 'needs_attention'}
          onClaim={onClaim}
          onSeeAll={() => setQueue('needs_attention')}
        />
      ) : null}

      <div className="ord-body">
        {isPhone ? null : (
          <OrdersRail counts={data.queue_counts} active={search.queue} onSelect={setQueue} />
        )}

        <div className="ord-main">
          {isPhone ? (
            <div className="ord-rail-phone">
              <OrdersRail counts={data.queue_counts} active={search.queue} onSelect={setQueue} />
            </div>
          ) : null}

          <Card
            title={QUEUE_LABELS[search.queue]}
            asOf={page.dataUpdatedAt > 0 ? new Date(page.dataUpdatedAt).toISOString() : null}
            ageClass="live"
            error={
              page.isError
                ? 'The last refresh failed. These are the rows from the previous one.'
                : null
            }
            onRetry={() => void page.refetch()}
            action={
              <div className="ord-tools">
                {rows.length > 0 ? (
                  <Chip
                    label={
                      selected.size === rows.length ? 'Clear selection' : `Select ${rows.length}`
                    }
                    active={selected.size === rows.length && rows.length > 0}
                    onClick={() =>
                      setSelected(
                        selected.size === rows.length
                          ? new Set<number>()
                          : new Set(rows.map((row) => row.order_id)),
                      )
                    }
                  />
                ) : null}
                {canExport ? (
                  <Button size="sm" variant="ghost" onClick={onExport} disabled={rows.length === 0}>
                    <DownloadIcon />
                    Export CSV
                  </Button>
                ) : null}
              </div>
            }
          >
            <div className="ord-rows" data-stale={stale ? 'true' : 'false'}>
              {isPhone ? (
                <OrderCardList
                  rows={rows}
                  onOpen={(row) => openOrder(row.order_id)}
                  handlers={handlers}
                  emptyMessage={`No orders in ${QUEUE_LABELS[search.queue]} for this search and range.`}
                />
              ) : (
                <OrdersTable
                  rows={rows}
                  queue={search.queue}
                  legend={legend}
                  sort={{ by: search.sort, dir: search.dir }}
                  onSortChange={(next) =>
                    setSearch({
                      ...search,
                      sort: next.by as OrdersSort,
                      dir: next.dir,
                    })
                  }
                  selected={selected}
                  onToggleSelected={(id) =>
                    setSelected((current) => {
                      const next = new Set(current);
                      if (next.has(id)) next.delete(id);
                      else next.add(id);
                      return next;
                    })
                  }
                  onOpen={(row) => openOrder(row.order_id)}
                  handlers={handlers}
                  loading={false}
                  emptyAction={emptyAction}
                />
              )}
            </div>

            {page.hasNextPage ? (
              <div className="ord-more">
                <Button
                  size="sm"
                  busy={page.isFetchingNextPage}
                  onClick={() => void page.fetchNextPage()}
                >
                  Load more
                </Button>
                <span className="ord-sub">
                  {/*
                    The queue counts are totals for the queue and do not narrow with the
                    search or the range, so quoting one next to a filtered row count would
                    read "45 of 1,842" on a search that matched 45 orders. The total is only
                    shown when it is the same question the rows are answering.
                  */}
                  {narrowed ? `${rows.length} shown` : `${rows.length} of ${data.queue_counts[search.queue] ?? rows.length} shown`}
                </span>
              </div>
            ) : null}
          </Card>
        </div>
      </div>

      <OrdersBulkBar
        selected={selectedIds}
        onClear={() => setSelected(new Set<number>())}
        onReprint={(docs) => void onBulkReprint(docs)}
        onStatus={canBulk ? (action) => void onBulkStatus(action) : undefined}
        busy={bulkBusy}
        results={bulkResults}
        onDismissResults={() => setBulkResults(null)}
      />

      <OrderDrawer
        orderId={orderId}
        onClose={() => openOrder(null)}
        section={drawerSection}
        slots={drawerSlots}
      />
    </div>
  );
}
